import React, {useState} from 'react';
import type {TableColumnsType} from 'antd';
import {Button, Card, Col, Radio, Row, Space, Table} from 'antd';
import { useTranslation } from 'react-i18next';

interface DriverData {
    key: string;
    driver: string;
    onDutyDays: number;
    deliveredOnTime: number;
    deliveredOverTime: number;
    damaged: number;
    missing: number;
    returned: number;
    delivering: number;
    parcelPerDay: number;
    avgDuration: string;
    finalDR: string;
    onTimeDR: string;
    firstAttemptDR: string;
    fakePOD: number;
    complaint: number;
}

const ReportsPage: React.FC = () => {
    const [dateFilter, setDateFilter] = useState<string>('thisMonth');
    const { t } = useTranslation();

    const driverData: DriverData[] = [
        {
            key: '1',
            driver: 'James Wilson',
            onDutyDays: 31,
            deliveredOnTime: 1438,
            deliveredOverTime: 35,
            damaged: 2,
            missing: 0,
            returned: 0,
            delivering: 100,
            parcelPerDay: 100,
            avgDuration: '18h12min',
            finalDR: '99.9%',
            onTimeDR: '99.9%',
            firstAttemptDR: '99.9%',
            fakePOD: 1,
            complaint: 0,
        },
        {
            key: '2',
            driver: 'Jane Doe',
            onDutyDays: 10,
            deliveredOnTime: 1374,
            deliveredOverTime: 17,
            damaged: 0,
            missing: 1,
            returned: 1,
            delivering: 73,
            parcelPerDay: 73,
            avgDuration: '18h12min',
            finalDR: '98.3%',
            onTimeDR: '98.3%',
            firstAttemptDR: '98.3%',
            fakePOD: 0,
            complaint: 1,
        },
        {
            key: '3',
            driver: 'Perter Peterson',
            onDutyDays: 21,
            deliveredOnTime: 1310,
            deliveredOverTime: 0,
            damaged: 0,
            missing: 0,
            returned: 0,
            delivering: 64,
            parcelPerDay: 64,
            avgDuration: '18h12min',
            finalDR: '99.8%',
            onTimeDR: '99.8%',
            firstAttemptDR: '99.8%',
            fakePOD: 0,
            complaint: 0,
        },
        {
            key: '4',
            driver: 'Davis Johnson',
            onDutyDays: 15,
            deliveredOnTime: 1293,
            deliveredOverTime: 0,
            damaged: 0,
            missing: 1,
            returned: 1,
            delivering: 10,
            parcelPerDay: 10,
            avgDuration: '18h12min',
            finalDR: '99.6%',
            onTimeDR: '99.6%',
            firstAttemptDR: '99.6%',
            fakePOD: 0,
            complaint: 0,
        },
        {
            key: '5',
            driver: 'William Brown',
            onDutyDays: 22,
            deliveredOnTime: 1281,
            deliveredOverTime: 0,
            damaged: 0,
            missing: 0,
            returned: 0,
            delivering: 20,
            parcelPerDay: 20,
            avgDuration: '18h12min',
            finalDR: '99.7%',
            onTimeDR: '99.7%',
            firstAttemptDR: '99.7%',
            fakePOD: 0,
            complaint: 0,
        },
    ];

    const columns: TableColumnsType<DriverData> = [
        {
            title: 'Driver',
            dataIndex: 'driver',
            key: 'driver',
            fixed: 'left',
            align: 'left',
        },
        {
            title: 'On-duty days',
            dataIndex: 'onDutyDays',
            key: 'onDutyDays',
            align: 'left',
        },
        {
            title: 'Delivered on time',
            dataIndex: 'deliveredOnTime',
            key: 'deliveredOnTime',
            align: 'left',
        },
        {
            title: 'Delivered over time',
            dataIndex: 'deliveredOverTime',
            key: 'deliveredOverTime',
            align: 'left',
        },
        {
            title: 'Damaged',
            dataIndex: 'damaged',
            key: 'damaged',
            align: 'left',
        },
        {
            title: 'Missing',
            dataIndex: 'missing',
            key: 'missing',
            align: 'left',
        },
        {
            title: 'Returned',
            dataIndex: 'returned',
            key: 'returned',
            align: 'left',
        },
        {
            title: 'Delivering',
            dataIndex: 'delivering',
            key: 'delivering',
            align: 'left',
        },
        {
            title: 'Parcel/Day',
            dataIndex: 'parcelPerDay',
            key: 'parcelPerDay',
            align: 'left',
        },
        {
            title: 'Avg Duration',
            dataIndex: 'avgDuration',
            key: 'avgDuration',
            align: 'left',
        },
        {
            title: 'Final DR',
            dataIndex: 'finalDR',
            key: 'finalDR',
            align: 'left',
        },
        {
            title: 'On-time DR',
            dataIndex: 'onTimeDR',
            key: 'onTimeDR',
            align: 'left',
        },
        {
            title: '1st Attempt DR',
            dataIndex: 'firstAttemptDR',
            key: 'firstAttemptDR',
            align: 'left',
        },
        {
            title: 'Fake POD',
            dataIndex: 'fakePOD',
            key: 'fakePOD',
            align: 'left',
        },
        {
            title: 'Complaint',
            dataIndex: 'complaint',
            key: 'complaint',
            align: 'left',
        },
    ];

    // 顶部汇总数据
    const summaryData = {
        parcels: {
            delivered: 14456,
            inTime: 14054,
            outOfTime: 402,
            missing: 1,
            damaged: 2,
            returned: 13,
            inDelivering: 25
        },
        throughput: {
            parcelsPerDay: 2235,
            routesPerDay: 11,
            parcelsPerRoutePerDay: 213
        },
        deliveryRate: {
            final: '99.6%',
            onTime: '98.5%',
            firstAttempt: '98.2%'
        },
        duration: {
            allDelivered: '18h24min',
            onTime: '14h35min',
            overtime: '26h03min'
        }
    };

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* 日期筛选 */}
                <div className="mb-6 flex justify-between">
                    <Radio.Group
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="mb-4"
                    >
                        <Radio.Button value="7days">7 days</Radio.Button>
                        <Radio.Button value="31days">31 days</Radio.Button>
                        <Radio.Button value="thisMonth" className="bg-purple-100 text-purple-700 border-purple-300">This
                            Month</Radio.Button>
                        <Radio.Button value="custom">□</Radio.Button>
                        <Space className="ml-4">
                            <span className="text-gray-500">Start</span>
                            <span>-</span>
                            <span className="text-gray-500">End</span>
                        </Space>
                    </Radio.Group>
                    <Button type="primary" className="bg-purple-600">Download</Button>
                </div>

                {/* 数据卡片部分 */}
                <Row gutter={[24, 24]} className="mb-8">
                    {/* 包裹数据卡片 */}
                    <Col span={8}>
                        <Card bordered={false} className="border-l-4 border-indigo-600 shadow-sm">
                            <div className="text-xl font-medium mb-4 text-indigo-600">Parcels</div>
                            <Row className="text-center" gutter={16}>
                                <Col span={8}>
                                    <div className="text-2xl font-semibold">{summaryData.parcels.delivered}</div>
                                    <div className="text-xs text-gray-500">Delivered</div>
                                </Col>
                                <Col span={4}>
                                    <div className="text-lg font-semibold">{summaryData.parcels.inTime}</div>
                                    <div className="text-xs text-gray-500">In time</div>
                                </Col>
                                <Col span={4}>
                                    <div className="text-lg font-semibold">{summaryData.parcels.outOfTime}</div>
                                    <div className="text-xs text-gray-500">Out of time</div>
                                </Col>
                                <Col span={2}>
                                    <div className="text-lg font-semibold">{summaryData.parcels.missing}</div>
                                    <div className="text-xs text-gray-500">Missing</div>
                                </Col>
                                <Col span={2}>
                                    <div className="text-lg font-semibold">{summaryData.parcels.damaged}</div>
                                    <div className="text-xs text-gray-500">Damaged</div>
                                </Col>
                                <Col span={2}>
                                    <div className="text-lg font-semibold">{summaryData.parcels.returned}</div>
                                    <div className="text-xs text-gray-500">Returned</div>
                                </Col>
                                <Col span={2}>
                                    <div className="text-lg font-semibold">{summaryData.parcels.inDelivering}</div>
                                    <div className="text-xs text-gray-500">In delivering</div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    {/* 吞吐量卡片 */}
                    <Col span={4}>
                        <Card bordered={false} className="border-l-4 border-indigo-600 shadow-sm h-full">
                            <div className="text-xl font-medium mb-4 text-indigo-600">Throughput</div>
                            <Row className="text-center" gutter={16}>
                                <Col span={8}>
                                    <div className="text-2xl font-semibold">{summaryData.throughput.parcelsPerDay}</div>
                                    <div className="text-xs text-gray-500">Parcels/Day</div>
                                </Col>
                                <Col span={8}>
                                    <div className="text-2xl font-semibold">{summaryData.throughput.routesPerDay}</div>
                                    <div className="text-xs text-gray-500">Routes/Day</div>
                                </Col>
                                <Col span={8}>
                                    <div
                                        className="text-2xl font-semibold">{summaryData.throughput.parcelsPerRoutePerDay}</div>
                                    <div className="text-xs text-gray-500">Parcels/Route/Day</div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    {/* 送达率卡片 */}
                    <Col span={6}>
                        <Card bordered={false} className="border-l-4 border-indigo-600 shadow-sm h-full">
                            <div className="text-xl font-medium mb-4 text-indigo-600">Delivery Rate</div>
                            <Row className="text-center" gutter={16}>
                                <Col span={8}>
                                    <div className="text-2xl font-semibold">{summaryData.deliveryRate.final}</div>
                                    <div className="text-xs text-gray-500">Final DR</div>
                                </Col>
                                <Col span={8}>
                                    <div className="text-2xl font-semibold">{summaryData.deliveryRate.onTime}</div>
                                    <div className="text-xs text-gray-500">On-time DR</div>
                                </Col>
                                <Col span={8}>
                                    <div
                                        className="text-2xl font-semibold">{summaryData.deliveryRate.firstAttempt}</div>
                                    <div className="text-xs text-gray-500">1st Attempt DR</div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    {/* 时间卡片 */}
                    <Col span={6}>
                        <Card bordered={false} className="border-l-4 border-indigo-600 shadow-sm h-full">
                            <div className="text-xl font-medium mb-4 text-indigo-600">Duration</div>
                            <Row className="text-center" gutter={16}>
                                <Col span={8}>
                                    <div className="text-xl font-semibold">{summaryData.duration.allDelivered}</div>
                                    <div className="text-xs text-gray-500">All deliverd parcels</div>
                                </Col>
                                <Col span={8}>
                                    <div className="text-xl font-semibold">{summaryData.duration.onTime}</div>
                                    <div className="text-xs text-gray-500">On-time parcels</div>
                                </Col>
                                <Col span={8}>
                                    <div className="text-xl font-semibold">{summaryData.duration.overtime}</div>
                                    <div className="text-xs text-gray-500">Overtime parcels</div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>

                {/* 驾驶员表格 */}
                <Table
                    columns={columns}
                    dataSource={driverData}
                    scroll={{x: 1500}}
                    pagination={false}
                    className="mt-4 mb-4"
                    size="middle"
                />
                <div className="flex justify-between items-center">
                    <div className="text-gray-500">
                        {t('table.pagination.totalItems', { total: driverData.length })}
                    </div>
                </div>
            </div>
    );
};

export default ReportsPage;
